// src/views/GrammarView.jsx
import { ArrowLeft, Menu, GraduationCap } from 'lucide-react';
import PropTypes from 'prop-types';
import { useGrammarLogic } from '../hooks/useGrammarLogic';
import GrammarSidebar from '../components/grammar/GrammarSidebar';
import GrammarTopicCard from '../components/grammar/GrammarTopicCard';

export default function GrammarView({ module, ACADEMY_DATA, setSelectedModule, setView, backView }) {
  const { isSidebarOpen, setSidebarOpen, topics, formatText } = useGrammarLogic(module);

  if (!module || !ACADEMY_DATA) {
    return <LoadingState setView={setView} backView={backView} />;
  }

  return (
    <div className="h-full flex flex-col bg-slate-50 relative w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">

      {/* HEADER AZUL */}
      <header className="bg-indigo-600 p-6 pt-8 pb-10 rounded-b-[2.5rem] shadow-xl relative z-20 text-white shrink-0">
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => setView(backView || 'lobby')} 
            className="p-2 bg-white/20 rounded-full hover:bg-white/30 active:scale-95 transition-all"
          >
            <ArrowLeft size={24} />
          </button>

          <div className="flex flex-col items-center">
            <span className="text-[10px] font-black uppercase tracking-widest bg-indigo-800/50 px-3 py-1 rounded-full mb-1">
              {module.id?.toUpperCase() || 'MÓDULO'}
            </span>
            <h1 className="text-xl font-black flex items-center gap-2">
              <GraduationCap size={24} /> 
              Grammar Reference
            </h1>
          </div>

          <button 
            onClick={() => setSidebarOpen(true)} 
            className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-all relative"
          >
            <Menu size={24} />
            <div className="absolute top-2 right-2 w-2 h-2 bg-amber-400 rounded-full animate-ping" />
          </button>
        </div>

        <div className="text-center px-4">
          <h2 className="text-2xl md:text-3xl font-black">{module.name}</h2>
          <p className="text-indigo-200 text-sm mt-2 opacity-80">
            {module.description || "Domina las reglas esenciales de este nivel."}
          </p>
        </div>
      </header>

      {/* SIDEBAR CON TEMARIO */}
      <GrammarSidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        modules={ACADEMY_DATA}
        currentModuleId={module.id}
        onSelect={(mod) => { 
          setSelectedModule(mod); 
          setSidebarOpen(false); 
        }}
      />

      {/* CONTENIDO PRINCIPAL */}
      <main className="flex-1 overflow-y-auto pb-32 px-4 md:px-6 -mt-6 pt-6 scroll-smooth">
        <div className="max-w-3xl mx-auto space-y-8">
          {topics.map((topic, index) => (
            <GrammarTopicCard 
              // ✅ FIX: Eliminadas las barras invertidas en el template literal
              key={`${topic.title}-${index}`}
              topic={topic} 
              index={index} 
              formatText={formatText} 
            />
          ))}

          {module.grammarImage && (
            <figure className="rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white mx-auto max-w-2xl transform hover:scale-[1.02] transition-transform">
              <img 
                src={module.grammarImage} 
                // ✅ FIX: Eliminadas las barras invertidas aquí también
                alt={`Diagrama para ${module.name}`}
                className="w-full h-auto" 
                loading="lazy"
              />
            </figure>
          )}
        </div>
      </main>
    </div>
  );
}

// Validación de Datos (Props)
GrammarView.propTypes = {
  module: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    grammarTopics: PropTypes.array,
    grammarText: PropTypes.string,
    grammarImage: PropTypes.string
  }),
  ACADEMY_DATA: PropTypes.object.isRequired,
  setSelectedModule: PropTypes.func.isRequired,
  setView: PropTypes.func.isRequired,
  backView: PropTypes.string
};

// Pantalla de Carga
function LoadingState({ setView, backView }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6 text-center animate-pulse">
      <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
      <h2 className="text-xl font-black text-slate-800">Cargando Conocimiento...</h2>
      <button 
        onClick={() => setView(backView || 'lobby')} 
        className="mt-6 bg-indigo-600 text-white px-8 py-3 rounded-2xl font-bold shadow-lg"
      >
        VOLVER
      </button>
    </div>
  );
}